/** Event + helpers to dock/undock Firefly with an optional fly animation. */

import {
  DOCK_POSITION_STORAGE_KEY,
  DEFAULT_POSITION,
  centerFromDockPosition,
  parseStoredPosition,
} from "@/components/activity/activityDockPositionMath";

export const FIREFLY_SIDEBAR_DOCK_REQUEST_EVENT = "bb-firefly-sidebar-dock-request";
export const FIREFLY_SIDEBAR_DOCK_TRAVEL_EVENT = "bb-firefly-sidebar-dock-travel";

export type FireflyDockTravelDirection = "to-sidebar" | "to-floating";

export type FireflySidebarDockRequestDetail = {
  /** Prefer fly animation when motion is allowed. */
  animate?: boolean;
  /** Default `to-sidebar` (dock in). `to-floating` undocks with reverse fly. */
  direction?: FireflyDockTravelDirection;
};

export type FireflySidebarDockTravelDetail = {
  active: boolean;
};

let dockTravelActive = false;
const travelListeners = new Set<(active: boolean) => void>();

export function isFireflySidebarDockTravelActive(): boolean {
  return dockTravelActive;
}

export function setFireflySidebarDockTravelActive(active: boolean): void {
  if (dockTravelActive === active) return;
  dockTravelActive = active;
  for (const listener of travelListeners) listener(active);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<FireflySidebarDockTravelDetail>(FIREFLY_SIDEBAR_DOCK_TRAVEL_EVENT, {
        detail: { active },
      }),
    );
  }
}

export function subscribeFireflySidebarDockTravel(listener: (active: boolean) => void): () => void {
  travelListeners.add(listener);
  return () => {
    travelListeners.delete(listener);
  };
}

export function dispatchFireflySidebarDockRequest(
  detail: FireflySidebarDockRequestDetail = { animate: true, direction: "to-sidebar" },
): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<FireflySidebarDockRequestDetail>(FIREFLY_SIDEBAR_DOCK_REQUEST_EVENT, {
      detail: {
        animate: detail.animate !== false,
        direction: detail.direction ?? "to-sidebar",
      },
    }),
  );
}

function centerOf(el: HTMLElement): { x: number; y: number } {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/**
 * Point centers for dock travel (viewport coords).
 * `to-sidebar`: floating tab → sidebar slot.
 * `to-floating`: sidebar slot → floating tab (or last known floating rest).
 */
export function resolveFireflyDockTravelPoints(
  direction: FireflyDockTravelDirection = "to-sidebar",
): {
  from: { x: number; y: number };
  to: { x: number; y: number };
} | null {
  if (typeof document === "undefined") return null;
  const floatingEl =
    document.querySelector<HTMLElement>('[data-firefly-target="activity-dock-tab"]') ??
    document.querySelector<HTMLElement>('[data-app-panel="ActivityDockFireflyTab"]');
  const sidebarEl =
    document.querySelector<HTMLElement>('[data-firefly-target="sidebar-firefly-dock"]') ??
    document.querySelector<HTMLElement>('[data-firefly-target="sidebar-settings"]') ??
    document.querySelector<HTMLElement>('[aria-label="Settings"]');

  if (direction === "to-sidebar") {
    if (!floatingEl || !sidebarEl) return null;
    const from = centerOf(floatingEl);
    const toRect = sidebarEl.getBoundingClientRect();
    const landingIsSettings =
      !document.querySelector('[data-firefly-target="sidebar-firefly-dock"]') &&
      (sidebarEl.getAttribute("data-firefly-target") === "sidebar-settings" ||
        sidebarEl.getAttribute("aria-label") === "Settings");
    return {
      from,
      to: {
        x: toRect.left + toRect.width / 2,
        y: landingIsSettings ? toRect.top - 20 : toRect.top + toRect.height / 2,
      },
    };
  }

  // Undock: fly from sidebar dock back to floating anchor.
  if (!sidebarEl) return null;
  const from = centerOf(sidebarEl);
  if (floatingEl) {
    return { from, to: centerOf(floatingEl) };
  }
  // Floating tab is hidden while docked — aim at persisted rest position.
  const rest =
    document.querySelector<HTMLElement>('[data-firefly-target="activity-dock-rest"]') ?? null;
  if (rest) return { from, to: centerOf(rest) };
  return { from, to: resolveStoredFloatingRestCenter() };
}

function resolveStoredFloatingRestCenter(): { x: number; y: number } {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  try {
    const raw =
      typeof localStorage !== "undefined" ? localStorage.getItem(DOCK_POSITION_STORAGE_KEY) : null;
    const pos = parseStoredPosition(raw) ?? DEFAULT_POSITION;
    let sidebarW = 0;
    if (typeof document !== "undefined") {
      const rawSidebar = getComputedStyle(document.documentElement)
        .getPropertyValue("--shell-sidebar-w")
        .trim();
      const n = Number.parseFloat(rawSidebar);
      if (Number.isFinite(n)) sidebarW = n;
    }
    return centerFromDockPosition(pos, vw, vh, undefined, sidebarW);
  } catch {
    return { x: vw - 56, y: vh - 96 };
  }
}
